import { Card, CardContent, Container, Grid, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Suspense, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetAccountsQuery } from '../../../../api/recalc/endpoints/accountsApiSlice';
import { useGetEnergyGroupsByUserQuery } from '../../../../api/recalcV2/endpoints/energyGroupsApiSlice';
import { useGetAllSystemQoSQuery } from '@/api/energy-analysis/systemQoSApiSlice';
import { getSelectedAccount } from '../../../../redux/selectedAccountSlice';
import EnergyGroupDrawerContainer from '@/layout/pages/ServiceManagement/energyxplorer/EnergyGroupDrawerContainer';
import EnergyXplorerDatagrid from '@/features/service-management/components/energyxplorer/EnergyXplorerDatagrid';

const EnergyXplorerManagementPage = () => {
  const [selectedRow, setSelectedRow] = useState({});
  const selectedAccount = useSelector(getSelectedAccount);

  const { data: accounts, isFetching } = useGetAccountsQuery();
  const { data: energyGroups } = useGetEnergyGroupsByUserQuery();
  const { data: systemQoS } = useGetAllSystemQoSQuery();

  const energyGroupsWithQoS = useMemo(() => {
    if (!energyGroups || !systemQoS) return [];
    return energyGroups.map((group) => {
      const qos =
        systemQoS.find((q) => q.EnergyGroupId === group.energyGroupId) || null;
      return {
        ...group,
        systemQoS: qos,
      };
    });
  }, [energyGroups, systemQoS]);

  const rows = useMemo(() => {
    let result = energyGroupsWithQoS ?? [];
    if (accounts === undefined || accounts?.length === 0) return [];

    result = result.map((g) => {
      let a = accounts.find((a) => g.accountId === a.AccountId);

      const initiativesCount = g.energyInitiatives.reduce((acc) => {
        return acc + 1;
      }, 0);

      const nodeCount = g.energyTreeNodes.reduce((acc) => {
        return acc + 1;
      }, 0);

      return {
        AccountId: a.AccountId,
        Account: a.Name,
        hasService:
          a.Services.length !== 0 &&
          a.Services.some((s) => s.Name === 'EnergyXplorer'),
        ...g,
        nodeCount,
        initiativesCount,
        id: g.energyGroupId,
        QoS: g?.systemQoS?.SystemQoSScore ?? null,
        ExecutionTime: g?.systemQoS?.ExecutionTime ?? null,
        OverallStatus: g?.systemQoS?.OverallStatus ?? null,
      };
    });
    return result;
  }, [accounts, energyGroupsWithQoS]);

  const stats = useMemo(() => {
    if (energyGroups === undefined || energyGroups?.length === 0) return [];

    const totalNodes = energyGroups.reduce((acc, g) => {
      return acc + g.energyTreeNodes.length;
    }, 0);

    const totalInitiatives = energyGroups.reduce((acc, g) => {
      return acc + g.energyInitiatives.length;
    }, 0);

    const totalEnergyGroups = energyGroups.length;

    const totalAccounts = new Set(energyGroups.map((g) => g.accountId)).size;
    return [
      { name: '# Noder', value: totalNodes },
      { name: '# Initiatives', value: totalInitiatives },
      { name: '# Energy groups', value: totalEnergyGroups },
      { name: '# Accounts', value: totalAccounts },
    ];
  }, [energyGroups]);

  return (
    <Container maxWidth="xxl">
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              verticalAlign: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <Box sx={{ display: 'flex', height: '70vh', width: '100%' }}>
          <Grid container spacing={2}>
            {stats.map((s, index) => (
              <Grid key={index} item lg={3}>
                <ServiceCard {...s} />
              </Grid>
            ))}
            <Grid item lg={12} maxHeight={'80vh'}>
              <EnergyXplorerDatagrid
                initialRows={rows}
                isLoading={isFetching}
                setSelectedRow={setSelectedRow}
                account={selectedAccount}
              />
            </Grid>
          </Grid>
        </Box>
      </Suspense>
      <EnergyGroupDrawerContainer
        isOpen={selectedRow?.Account}
        onClose={() => setSelectedRow({})}
        energyGroup={selectedRow}
      />
    </Container>
  );
};

export default EnergyXplorerManagementPage;

const ServiceCard = (props) => {
  const { name, value } = props;

  return (
    <Card variant="outlined">
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            mb: 2,
          }}
        >
          <Typography variant="body1" color="textSecondary" fontWeight="bold">
            {name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
          >
            <Typography variant="h4" color="textPrimary" fontWeight={'bold'}>
              {value}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
